import React from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme, Styles } from 'react-jss';
import { Theme, Colors } from '@app/theme';
import Color from 'color';
import mapValues from 'lodash/mapValues';

export type ChipColor = Colors;

const useStyles = createUseStyles<string, {}, any>((theme: Theme) => ({
  chip: {
    ...theme.fonts.label,
    display: 'inline-flex',
    alignItems: 'center',

    minHeight: theme.height.chips,
    borderRadius: theme.borderRadius.std,

    padding: [[0, theme.marginBase]],
  },
  ...theme.mutedColorCombinations,
  whiteBg: {
    backgroundColor: 'white',
  },
  round: {
    borderRadius: theme.height.chips / 2,
  },
}));

const useSelectableStyle = createUseStyles(
  (theme: Theme): Styles<Colors, {}, undefined> =>
    mapValues(theme.basicColorsDefinition, (colors) => ({
      backgroundColor: 'transparent',

      color: theme.colors.inkTertiary,
      border: {
        color: theme.colors.inkTertiary,
        width: 1,
        style: 'solid',
      },
      opacity: 0.8,

      '&:hover': {
        backgroundColor: colors.muted.fade(0.5).hex(),
      },
      '&.selected': {
        backgroundColor: colors.muted.hex(),
        color: colors.accessible.hex(),
        borderColor: colors.accessible.hex(),
        opacity: 1,
      },
    })),
);

const useInvertedStyle = createUseStyles(
  (theme: Theme): Styles<Colors, {}, undefined> =>
    mapValues(theme.basicColorsDefinition, (colors) => ({
      backgroundColor: colors.accessible.hex(),
      color: theme.colors.white,
    })),
);

interface Props {
  color?: ChipColor;
  customColor?: string;
  className?: string;
  children?: React.ReactNode;
  overGray?: boolean;
  variant?: 'default' | 'selectable' | 'round' | 'inverted';
  selected?: boolean;
}

export const Chip = ({
  color = 'ink',
  className,
  overGray,
  customColor,
  variant = 'default',
  selected,
  ...rest
}: Props) => {
  const theme: Theme = useTheme() as Theme;
  const classes = useStyles({ theme });
  const selectableClasses = useSelectableStyle({ theme });
  const invertedClasses = useInvertedStyle({ theme });

  let predefinedColor = color;
  let style = undefined;
  if (customColor) {
    if (Object.keys(classes).includes(customColor)) {
      predefinedColor = customColor as ChipColor;
    } else {
      const bgColor = new Color(customColor);
      style = {
        backgroundColor: bgColor.hex(),
        color: bgColor.isLight() ? theme.colors.inkPrimary : theme.colors.white,
      };
    }
  }

  if (variant === 'selectable') {
    return (
      <div
        role="option"
        aria-selected={selected}
        className={classnames(
          classes.chip,
          selectableClasses[predefinedColor],
          selected ? 'selected' : undefined,
          className,
        )}
        {...rest}
      />
    );
  }

  return (
    <div
      className={classnames(
        classes.chip,
        {
          [classes[predefinedColor]]: variant !== 'inverted',
          [invertedClasses[predefinedColor]]: variant === 'inverted',
          [classes.whiteBg]:
            overGray &&
            (predefinedColor === 'ink' || predefinedColor === 'inkSecondary'),

          [classes.round]: variant === 'round',
        },
        className,
      )}
      style={style}
      {...rest}
    />
  );
};
